/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Image, Text, UnstyledButton } from '@/shared/ui';
import { CheckIcon, ExitIcon } from '@/shared/ui/radix-icons';
import { getInitials } from '@/shared/utils/get-initials';
import { changeLanguage, getAvailableLanguages } from '@/shared/i18n';
import { cn } from '@/shared/ui/utils/tailwind';

const LANGUAGE_LABELS: Record<string, string> = {
  'pt-BR': 'Português', // i18n-exempt: endonym, named in its own language
  en: 'English', // i18n-exempt: endonym, named in its own language
  es: 'Español', // i18n-exempt: endonym, named in its own language
};

/**
 * Normalizes a language string to a supported locale code.
 *
 * @param lang - Raw i18next language string.
 * @returns One of the supported locale codes.
 */
export const normalizeLanguage = (lang: string): string => {
  if (!lang) return 'pt-BR';
  const lower = lang.toLowerCase();
  if (lower.startsWith('pt')) return 'pt-BR';
  if (lower.startsWith('en')) return 'en';
  if (lower.startsWith('es')) return 'es';
  return 'pt-BR';
};

type IdentityProps = {
  fullName: string;
  email: string;
  photoUrl?: string;
};

/**
 * Account identity block: avatar (photo or initials), display name and email.
 *
 * @param props.fullName - User display name.
 * @param props.email - User email address.
 * @param props.photoUrl - Optional profile picture URL.
 * @returns The identity row.
 */
export const UserSettingsIdentity = ({ fullName, email, photoUrl }: IdentityProps) => (
  <div
    className="flex items-center gap-(--space-3) px-(--space-3) py-(--space-3) min-w-0"
    data-testid="user-settings-identity"
  >
    <div className="w-10 h-10 rounded-full flex items-center justify-center bg-(--seed-base) text-white! text-(length:--text-body-sm) font-(--font-bold) overflow-hidden shrink-0">
      {photoUrl ? (
        <Image src={photoUrl} alt={fullName} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
      ) : (
        getInitials(fullName)
      )}
    </div>
    <div className="flex flex-col gap-(--space-0-5) min-w-0">
      <Text className="text-(length:--text-body-sm) font-(--font-semibold) text-(--text-primary) truncate">
        {fullName}
      </Text>
      <Text className="text-(length:--text-meta) text-(--text-tertiary) truncate">{email}</Text>
    </div>
  </div>
);

/**
 * Compact language switcher: one row per available locale, check mark on the
 * active one. Sized to sit in the settings rail without a nested panel.
 *
 * @param props.currentLanguage - Currently active i18next language.
 * @returns The language switcher block.
 */
export const UserSettingsLanguage = ({ currentLanguage }: { currentLanguage: string }) => {
  const { t } = useTranslation('@pages/user-settings');
  const active = normalizeLanguage(currentLanguage);

  return (
    <div className="flex flex-col gap-(--space-0-5)" data-testid="user-settings-language">
      <Text className="px-(--space-3) pb-(--space-1) text-(length:--text-meta) font-(--font-semibold) uppercase tracking-(--tracking-wider) text-(--text-tertiary)">
        {t('nav.language')}
      </Text>
      {getAvailableLanguages().map((lang) => {
        const isActive = active === lang;
        return (
          <UnstyledButton
            key={lang}
            onClick={() => changeLanguage(lang)}
            aria-current={isActive ? 'true' : undefined}
            className={cn(
              'flex items-center justify-between w-full h-9 px-(--space-3) rounded-(--radius-md) cursor-pointer',
              'transition-[background-color] duration-150 hover:bg-(--surface-hover)',
              isActive && 'bg-(--selection-active)',
            )}
          >
            <Text
              component="span"
              className={cn(
                'text-(length:--text-body-sm) text-(--text-primary)',
                isActive && 'text-(--seed-base) font-(--font-medium)',
              )}
            >
              {LANGUAGE_LABELS[lang] || lang}
            </Text>
            {isActive && <CheckIcon className="w-4 h-4 text-(--seed-base) shrink-0" />}
          </UnstyledButton>
        );
      })}
    </div>
  );
};

/**
 * Sign-out control for the settings modal footer.
 *
 * @param props.onSignOut - Invoked when the user confirms sign out.
 * @returns The sign-out button.
 */
export const UserSettingsSignOut = ({ onSignOut }: { onSignOut: () => void }) => {
  const { t } = useTranslation('@pages/user-settings');

  return (
    <UnstyledButton
      onClick={onSignOut}
      data-testid="user-settings-sign-out"
      className="flex items-center gap-(--space-2) w-full h-9 px-(--space-3) rounded-(--radius-md) cursor-pointer transition-[background-color] duration-150 hover:bg-(--surface-hover)"
      style={{ color: 'var(--text-primary)' }}
    >
      {/*
       * Signing out is reversible — the alarm red this used to carry is
       * reserved for destructive actions. Brand ink on the glyph and plain
       * primary on the label match every other row in this rail. The glyph
       * is mirrored so the arrow leaves to the left; unmirrored it points
       * into the frame and reads as signing in.
       */}
      <ExitIcon
        width={18}
        height={18}
        className="shrink-0"
        style={{ color: 'var(--seed-base)', transform: 'scaleX(-1)' }}
      />
      <Text component="span" className="text-(length:--text-body-sm)" style={{ color: 'inherit' }}>
        {t('nav.signOut')}
      </Text>
    </UnstyledButton>
  );
};
